import React, { useState } from "react";
import { Input } from "./ui/input";

/**
 * A currency field that reads as money.
 *
 * While the field has focus it shows the plain number, so grouping separators
 * never fight the cursor mid-edit. As soon as focus leaves it renders as
 * `$100,000.00`. Anything a person might reasonably type or paste, symbols and
 * commas included, is accepted.
 *
 * Shared rather than reimplemented per screen: the form and the costs table
 * previously formatted differently, which is exactly the drift this avoids.
 */
export const formatMoney = (value: number | string | null | undefined) => {
  const amount = Number(value ?? 0);
  return Number.isFinite(amount)
    ? amount.toLocaleString("en-AU", { style: "currency", currency: "AUD" })
    : "$0.00";
};

export const parseMoney = (value: string) => {
  const cleaned = value.replace(/[^0-9.-]/g, "");
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
};

export default function MoneyInput({
  value,
  onChange,
  className = "",
  id,
  name,
  required = false,
  placeholder = "$0.00",
  "aria-label": ariaLabel,
}: {
  value: number | string | null | undefined;
  onChange: (value: number) => void;
  className?: string;
  id?: string;
  name?: string;
  required?: boolean;
  placeholder?: string;
  "aria-label"?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const numeric = Number(value ?? 0);
  const hasValue = value !== "" && value !== null && value !== undefined;

  return (
    <Input
      id={id}
      name={name}
      className={className}
      inputMode="decimal"
      required={required}
      placeholder={placeholder}
      aria-label={ariaLabel}
      value={editing ? draft : hasValue ? formatMoney(numeric) : ""}
      onFocus={() => {
        setDraft(hasValue && numeric !== 0 ? String(numeric) : "");
        setEditing(true);
      }}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => {
        setEditing(false);
        onChange(draft.trim() === "" ? 0 : parseMoney(draft));
      }}
    />
  );
}

export const formatPercent = (value: number | string | null | undefined) => {
  const amount = Number(value ?? 0);
  if (!Number.isFinite(amount)) return "0%";
  // Trailing zeros are noise on a percentage: 10% not 10.00%.
  return `${Number(amount.toFixed(2))}%`;
};

/**
 * The percentage twin of {@link MoneyInput}, so a discount reads the same way
 * whichever unit it is in: bare while typing, with its symbol once you leave.
 */
export function PercentInput({
  value,
  onChange,
  className = "",
  id,
  name,
  "aria-label": ariaLabel,
}: {
  value: number | string | null | undefined;
  onChange: (value: number) => void;
  className?: string;
  id?: string;
  name?: string;
  "aria-label"?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const numeric = Number(value ?? 0);
  const hasValue = value !== "" && value !== null && value !== undefined;

  return (
    <Input
      id={id}
      name={name}
      className={className}
      inputMode="decimal"
      placeholder="0%"
      aria-label={ariaLabel}
      value={editing ? draft : hasValue ? formatPercent(numeric) : ""}
      onFocus={() => {
        setDraft(hasValue && numeric !== 0 ? String(numeric) : "");
        setEditing(true);
      }}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => {
        setEditing(false);
        const parsed = Number(draft.replace(/[^0-9.]/g, ""));
        // A discount over 100% would make the charge negative; the database
        // refuses it, so stop it here rather than surfacing a constraint error.
        onChange(Number.isFinite(parsed) ? Math.min(parsed, 100) : 0);
      }}
    />
  );
}
